const GEO_CACHE_PREFIX = 'deepsage:geo-country:v2:';
const GEO_LOOKUP_TIMEOUT_MS = 6000;
const IPV4_RE = /^(?:\d{1,3}\.){3}\d{1,3}$/;

const GEO_PROBE_EACH_MS = 2500;
/** 与大陆出口探测一致：VPN 切换后不能把旧国家码用整段会话 */
const GEO_CACHE_TTL_MS = 15_000;
const CURRENT_IP_CACHE_KEY = '__current__';
/** Cloudflare 非国家 loc */
const NON_COUNTRY_LOCS = new Set(['T1', 'XX', 'A1', 'A2', 'O1']);
/** 易被标成 CN 的地区；多源探测时优先于 CN */
const SPECIAL_REGION_CODES = new Set(['HK', 'MO', 'TW']);
/** 业务/旧码 → ISO alpha-2。UK 不是 ISO 码，但 ip/支付列表经常用。 */
const ALPHA2_ALIASES = {
  UK: 'GB',
  EL: 'GR',
};
const ALPHA3_TO_ALPHA2 = {
  ARG: 'AR',
  AUS: 'AU',
  AUT: 'AT',
  BEL: 'BE',
  BGD: 'BD',
  BRA: 'BR',
  CAN: 'CA',
  CHE: 'CH',
  CHL: 'CL',
  CHN: 'CN',
  COL: 'CO',
  CZE: 'CZ',
  DEU: 'DE',
  DNK: 'DK',
  EGY: 'EG',
  ESP: 'ES',
  FIN: 'FI',
  FRA: 'FR',
  GBR: 'GB',
  GRC: 'GR',
  HKG: 'HK',
  HUN: 'HU',
  IDN: 'ID',
  IND: 'IN',
  IRL: 'IE',
  ISR: 'IL',
  ITA: 'IT',
  JPN: 'JP',
  KOR: 'KR',
  KWT: 'KW',
  MAC: 'MO',
  MEX: 'MX',
  MYS: 'MY',
  NGA: 'NG',
  NLD: 'NL',
  NOR: 'NO',
  NZL: 'NZ',
  PAK: 'PK',
  PER: 'PE',
  PHL: 'PH',
  POL: 'PL',
  PRT: 'PT',
  QAT: 'QA',
  ROU: 'RO',
  RUS: 'RU',
  SAU: 'SA',
  SGP: 'SG',
  SWE: 'SE',
  THA: 'TH',
  TUR: 'TR',
  TWN: 'TW',
  UKR: 'UA',
  ARE: 'AE',
  USA: 'US',
  VNM: 'VN',
  ZAF: 'ZA',
};
const COUNTRY_NAME_TO_ALPHA2 = {
  'HONG KONG': 'HK',
  HONGKONG: 'HK',
  香港: 'HK',
  香港特别行政区: 'HK',
  香港特別行政區: 'HK',
  MACAU: 'MO',
  MACAO: 'MO',
  澳门: 'MO',
  澳門: 'MO',
  TAIWAN: 'TW',
  台湾: 'TW',
  台灣: 'TW',
  'UNITED KINGDOM': 'GB',
  'GREAT BRITAIN': 'GB',
  BRITAIN: 'GB',
  英国: 'GB',
  英國: 'GB',
  SINGAPORE: 'SG',
  新加坡: 'SG',
  VIETNAM: 'VN',
  'VIET NAM': 'VN',
  越南: 'VN',
  JAPAN: 'JP',
  日本: 'JP',
  'SOUTH KOREA': 'KR',
  KOREA: 'KR',
  韩国: 'KR',
  韓國: 'KR',
  'UNITED STATES': 'US',
  美国: 'US',
  美國: 'US',
  BRAZIL: 'BR',
  BRASIL: 'BR',
  巴西: 'BR',
  MEXICO: 'MX',
  MÉXICO: 'MX',
  墨西哥: 'MX',
};

/** @typedef {(ip: string, signal: AbortSignal) => Promise<string | null>} GeoIpProbe */

let currentIpLookupSeq = 0;
/** @type {Promise<string | null> | null} */
let currentIpInFlight = null;
let geoLifecycleAttached = false;

function invalidateCurrentIpCountryCache() {
  purgeGeoCache(CURRENT_IP_CACHE_KEY);
  currentIpLookupSeq += 1;
  currentIpInFlight = null;
}

function attachGeoLifecycleRefresh() {
  if (geoLifecycleAttached || typeof window === 'undefined') return;
  geoLifecycleAttached = true;
  const invalidate = () => invalidateCurrentIpCountryCache();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') invalidate();
  });
  window.addEventListener('online', invalidate);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) invalidate();
  });
}

function normalizeIp(ip) {
  return String(ip ?? '').trim();
}

function looksLikeHongKong(text) {
  const raw = String(text ?? '').trim();
  if (!raw) return false;
  if (raw.includes('香港')) return true;
  return /\bHONG\s*KONG\b/i.test(raw) || /HONGKONG/i.test(raw);
}

function looksLikeMacau(text) {
  const raw = String(text ?? '').trim();
  if (!raw) return false;
  if (raw.includes('澳门') || raw.includes('澳門')) return true;
  return /\bMACAU\b/i.test(raw) || /\bMACAO\b/i.test(raw);
}

function looksLikeTaiwan(text) {
  const raw = String(text ?? '').trim();
  if (!raw) return false;
  if (raw.includes('台湾') || raw.includes('台灣')) return true;
  return /\bTAIWAN\b/i.test(raw);
}

function specialRegionFromText(text) {
  if (looksLikeHongKong(text)) return 'HK';
  if (looksLikeMacau(text)) return 'MO';
  if (looksLikeTaiwan(text)) return 'TW';
  return null;
}

/**
 * ISO 3166-1 alpha-2，兼容 UK/HKG/Hong Kong 等别名。
 * @param {unknown} code
 * @returns {string | null}
 */
function normalizeCountryCode(code) {
  const raw = String(code ?? '').trim();
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (NON_COUNTRY_LOCS.has(upper)) return null;
  if (/^[A-Z]{2}$/.test(upper)) return ALPHA2_ALIASES[upper] || upper;
  if (/^[A-Z]{3}$/.test(upper) && ALPHA3_TO_ALPHA2[upper]) return ALPHA3_TO_ALPHA2[upper];
  return COUNTRY_NAME_TO_ALPHA2[upper] || COUNTRY_NAME_TO_ALPHA2[raw] || null;
}

/**
 * 多源探测：多数一致的国家码；仅当结果只有 CN + 港/澳/台时，地区码吃掉 CN。
 * 日本等其它国家出现时，不能让一个过期的 HK 探测压过 JP。
 * @param {Array<string | null | undefined>} codes
 * @returns {string | null}
 */
function selectBestGeoCountryCode(codes) {
  const list = (Array.isArray(codes) ? codes : [])
    .map((code) => normalizeCountryCode(code))
    .filter(Boolean);
  if (!list.length) return null;

  /** @type {Map<string, number>} */
  const counts = new Map();
  for (const code of list) {
    counts.set(code, (counts.get(code) || 0) + 1);
  }

  const specials = [...SPECIAL_REGION_CODES].filter((code) => counts.has(code));
  const onlyCnAndSpecial =
    specials.length > 0 &&
    [...counts.keys()].every((code) => code === 'CN' || SPECIAL_REGION_CODES.has(code));
  if (onlyCnAndSpecial && counts.has('CN')) {
    const special = specials[0];
    counts.set(special, counts.get(special) + counts.get('CN'));
    counts.delete('CN');
  }

  let bestCount = 0;
  for (const n of counts.values()) {
    if (n > bestCount) bestCount = n;
  }
  const tied = new Set(
    [...counts.entries()].filter(([, n]) => n === bestCount).map(([code]) => code)
  );
  return list.find((code) => tied.has(code)) ?? list[0];
}

/**
 * 从 ipwho / ipapi / country.is JSON 取国家码；CN + 港/澳/台地区按对应码。
 * @param {unknown} data
 * @returns {string | null}
 */
function countryCodeFromGeoPayload(data) {
  if (!data || typeof data !== 'object') return null;
  const map = /** @type {Record<string, unknown>} */ (data);
  const direct = normalizeCountryCode(
    map.country_code ?? map.countryCode ?? map.country ?? map.loc ?? map.iso_code
  );
  const regionBlob = [map.region, map.region_name, map.city, map.region_code, map.country]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
    .join(' ');
  return specialRegionFromText(regionBlob) || direct;
}

/**
 * country.is 现多为 JSON：{"ip":"...","country":"HK"}；兼容纯文本 HK。
 * @param {string} text
 * @returns {string | null}
 */
function parseCountryIsText(text) {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return null;
  const direct = normalizeCountryCode(trimmed);
  if (direct) return direct;
  try {
    return countryCodeFromGeoPayload(JSON.parse(trimmed));
  } catch {
    return null;
  }
}

/**
 * @param {string} text
 * @returns {string | null}
 */
function parseCloudflareTraceCountry(text) {
  const lines = String(text ?? '').split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line.startsWith('loc=')) continue;
    return normalizeCountryCode(line.slice(4));
  }
  return null;
}

function countIpv6Groups(segment) {
  return segment ? segment.split(':').filter(Boolean).length : 0;
}

function isIpv6Format(ip) {
  const normalized = String(ip ?? '').trim();
  if (!normalized.includes(':')) return false;
  if (!/^[0-9a-fA-F:.]+$/.test(normalized)) return false;
  if (normalized.length > 45) return false;

  const colonCount = (normalized.match(/:/g) || []).length;
  if (colonCount > 7) return false;

  const halves = normalized.split('::');
  if (halves.length > 2) return false;

  if (halves.length === 1) {
    return countIpv6Groups(halves[0]) === 8;
  }

  const totalGroups = countIpv6Groups(halves[0]) + countIpv6Groups(halves[1]);
  return totalGroups > 0 && totalGroups < 8;
}

function isNonGeoIpv4(ip) {
  const parts = ip.split('.').map((part) => Number(part));
  if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part) || part > 255)) return true;
  const [a, b] = parts;
  if (a === 127 || a === 10 || a === 0) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 169 && b === 254) return true;
  return false;
}

function isNonGeoIpv6(ip) {
  const lower = ip.toLowerCase();
  if (lower === '::1' || lower === '::') return true;
  if (lower.startsWith('fe80:')) return true;
  if (/^f[cd][0-9a-f]{2}:/i.test(lower)) return true;
  return false;
}

function isValidGeoIp(ip) {
  const normalized = normalizeIp(ip);
  if (!normalized) return false;
  if (IPV4_RE.test(normalized)) {
    return !isNonGeoIpv4(normalized);
  }
  if (isIpv6Format(normalized)) {
    return !isNonGeoIpv6(normalized);
  }
  return false;
}

function readGeoCacheRaw(ip) {
  if (typeof sessionStorage === 'undefined') return '';
  try {
    return String(sessionStorage.getItem(`${GEO_CACHE_PREFIX}${ip}`) ?? '').trim();
  } catch {
    return '';
  }
}

function purgeGeoCache(ip) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(`${GEO_CACHE_PREFIX}${ip}`);
  } catch {
    // ignore
  }
}

function readValidatedGeoCache(ip) {
  const raw = readGeoCacheRaw(ip);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const countryCode = normalizeCountryCode(parsed?.countryCode);
    const at = Number(parsed?.at);
    if (!countryCode || !Number.isFinite(at) || Date.now() - at > GEO_CACHE_TTL_MS) {
      purgeGeoCache(ip);
      return null;
    }
    return countryCode;
  } catch {
    // 旧版只存国家码、没有过期时间，VPN 切换后会一直沿用
    purgeGeoCache(ip);
    return null;
  }
}

function writeGeoCache(ip, countryCode) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      `${GEO_CACHE_PREFIX}${ip}`,
      JSON.stringify({ countryCode, at: Date.now() })
    );
  } catch {
    // ignore quota / private mode
  }
}

async function fetchCountryFromIpapi(ip, signal) {
  const res = await fetch(`https://ipapi.co/${encodeURIComponent(ip)}/country_code/`, {
    signal,
    headers: { Accept: 'text/plain' },
  });
  if (!res.ok) return null;
  return normalizeCountryCode(await res.text());
}

async function fetchCountryFromIpwho(ip, signal) {
  const res = await fetch(`https://ipwho.is/${encodeURIComponent(ip)}`, { signal });
  if (!res.ok) return null;
  const data = await res.json();
  if (data?.success === false) return null;
  return countryCodeFromGeoPayload(data);
}

async function fetchCountryFromGeoJs(ip, signal) {
  const res = await fetch(`https://get.geojs.io/v1/ip/country/${encodeURIComponent(ip)}.json`, {
    signal,
  });
  if (!res.ok) return null;
  const data = await res.json();
  return normalizeCountryCode(data?.country);
}

async function fetchCountryFromIpapiCurrent(signal) {
  const res = await fetch('https://ipapi.co/json/', { signal });
  if (!res.ok) return null;
  const data = await res.json();
  if (data?.error) return null;
  return countryCodeFromGeoPayload(data);
}

async function fetchCountryFromIpwhoCurrent(signal) {
  const res = await fetch('https://ipwho.is/', { signal });
  if (!res.ok) return null;
  const data = await res.json();
  if (data?.success === false) return null;
  return countryCodeFromGeoPayload(data);
}

async function fetchCountryFromGeoJsCurrent(signal) {
  const res = await fetch('https://get.geojs.io/v1/ip/country.json', { signal });
  if (!res.ok) return null;
  const data = await res.json();
  return normalizeCountryCode(data?.country);
}

async function fetchCountryFromCountryIsCurrent(signal) {
  const res = await fetch('https://api.country.is/', {
    signal,
    headers: { Accept: 'application/json, text/plain, */*' },
  });
  if (!res.ok) return null;
  return parseCountryIsText(await res.text());
}

async function fetchCountryFromCloudflareCurrent(url, signal) {
  const res = await fetch(url, {
    signal,
    headers: { Accept: 'text/plain' },
  });
  if (!res.ok) return null;
  return parseCloudflareTraceCountry(await res.text());
}

/** @type {GeoIpProbe[]} */
const GEO_IP_PROBES = [fetchCountryFromIpapi, fetchCountryFromIpwho, fetchCountryFromGeoJs];

/** @type {Array<(signal: AbortSignal) => Promise<string | null>>} */
const CURRENT_IP_PROBES = [
  (signal) => fetchCountryFromCloudflareCurrent('https://cloudflare.com/cdn-cgi/trace', signal),
  (signal) => fetchCountryFromCloudflareCurrent('https://1.1.1.1/cdn-cgi/trace', signal),
  fetchCountryFromIpwhoCurrent,
  fetchCountryFromIpapiCurrent,
  fetchCountryFromGeoJsCurrent,
  fetchCountryFromCountryIsCurrent,
];

async function runGeoProbes(probes, ip, signal) {
  for (const probe of probes) {
    try {
      const countryCode = await probe(ip, signal);
      if (countryCode) return countryCode;
    } catch {
      // try next probe
    }
  }
  return null;
}

async function probeCurrentWithTimeout(probe, parentSignal) {
  const controller = new AbortController();
  const onParentAbort = () => controller.abort();
  if (parentSignal) {
    if (parentSignal.aborted) return null;
    parentSignal.addEventListener('abort', onParentAbort);
  }
  const timer = setTimeout(() => controller.abort(), GEO_PROBE_EACH_MS);
  try {
    return await probe(controller.signal);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
    parentSignal?.removeEventListener('abort', onParentAbort);
  }
}

async function runCurrentIpProbes(signal) {
  const codes = await Promise.all(
    CURRENT_IP_PROBES.map((probe) => probeCurrentWithTimeout(probe, signal))
  );
  return selectBestGeoCountryCode(codes);
}

/**
 * 将 IP 解析为 ISO 3166-1 alpha-2 国家码；失败返回 null。
 * @param {string | null | undefined} ip
 * @returns {Promise<string | null>}
 */
export async function resolveCountryCodeFromIp(ip) {
  const normalized = normalizeIp(ip);
  if (!normalized || !isValidGeoIp(normalized)) return null;

  const cached = readValidatedGeoCache(normalized);
  if (cached) return cached;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), GEO_LOOKUP_TIMEOUT_MS);

  try {
    const countryCode = await runGeoProbes(GEO_IP_PROBES, normalized, controller.signal);
    if (countryCode) writeGeoCache(normalized, countryCode);
    return countryCode;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * 探测当前出口 IP 的国家码（无 loginIp 或 lookup 失败时兜底）。
 * @param {{ forceRefresh?: boolean }} [options]
 * @returns {Promise<string | null>}
 */
export async function resolveCountryCodeFromCurrentIp({ forceRefresh = false } = {}) {
  attachGeoLifecycleRefresh();

  if (!forceRefresh) {
    const cached = readValidatedGeoCache(CURRENT_IP_CACHE_KEY);
    if (cached) return cached;
    if (currentIpInFlight) return currentIpInFlight;
  } else {
    purgeGeoCache(CURRENT_IP_CACHE_KEY);
    currentIpLookupSeq += 1;
  }

  const seq = currentIpLookupSeq;
  const lookup = (async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), GEO_LOOKUP_TIMEOUT_MS);
    try {
      const countryCode = await runCurrentIpProbes(controller.signal);
      if (seq === currentIpLookupSeq && countryCode) {
        writeGeoCache(CURRENT_IP_CACHE_KEY, countryCode);
      }
      return countryCode;
    } finally {
      clearTimeout(timer);
    }
  })();

  currentIpInFlight = lookup.finally(() => {
    if (currentIpInFlight === lookup) currentIpInFlight = null;
  });
  return currentIpInFlight;
}

/**
 * @param {string | null | undefined} code
 * @returns {boolean}
 */
export function countryCodeIsUnitedStates(code) {
  return normalizeCountryCode(code) === 'US';
}

export function countryCodeIsVietnam(code) {
  return normalizeCountryCode(code) === 'VN';
}

export function countryCodeIsSingapore(code) {
  return normalizeCountryCode(code) === 'SG';
}

export function countryCodeIsBrazil(code) {
  return normalizeCountryCode(code) === 'BR';
}

export function countryCodeIsMexico(code) {
  return normalizeCountryCode(code) === 'MX';
}

export function countryCodeIsIndia(code) {
  return normalizeCountryCode(code) === 'IN';
}

/**
 * 越南本地支付是否优先展示：资料 VN 或当前出口 IP VN（或关系，不作硬门禁）。
 * @param {{ profileCountryCode?: string | null, currentIpCountryCode?: string | null }} [input]
 */
export function isVietnamPayPreferred({ profileCountryCode, currentIpCountryCode } = {}) {
  return countryCodeIsVietnam(profileCountryCode) || countryCodeIsVietnam(currentIpCountryCode);
}

/**
 * 同步读当前出口 IP 国家码缓存（未命中或过期返回 null）。
 * 打开充值弹窗时用来立刻套法币，避免现金页先闪 USD。
 */
export function peekCachedCurrentIpCountryCode() {
  return readValidatedGeoCache(CURRENT_IP_CACHE_KEY);
}

export {
  isValidGeoIp,
  normalizeCountryCode,
  countryCodeFromGeoPayload,
  parseCountryIsText,
  parseCloudflareTraceCountry,
  selectBestGeoCountryCode,
};
